import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

/**
 * Origin the API actually lives on, for building absolute links we hand to
 * something outside the browser (the mkpaceLIVE watch, a printout, a QR code).
 *
 * It must be derived from the API base rather than from window.location: in dev
 * the app is served from :4215 while the API is on :8001, and the dev server
 * answers unknown paths with index.html and a 200 — so a URL built from the page
 * origin looks like it works and silently returns HTML. In production the base is
 * the relative "/api", which resolves to the page origin, which is correct there.
 */
export const apiOrigin = () => {
  try {
    return new URL(API_BASE_URL, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle authentication errors globally
api.interceptors.response.use(
  (response) => {
    // If response is successful, just return it
    return response;
  },
  (error) => {
    // 401 ONLY. A 403 means authenticated-but-not-permitted — a club file you are
    // not an admin for, someone else's link, an admin-only endpoint — and the
    // backend never uses it for auth failures (get_current_active_user raises 401).
    // Treating it as an expired token logged the user out mid-task on any ordinary
    // permission error.
    if (error.response?.status === 401) {
      // Get the current token
      const token = localStorage.getItem('token');

      // Only handle auth errors if we had a token (user was logged in)
      // This prevents clearing tokens on initial login failures
      if (token && error.config && !error.config.url.includes('/auth/login')) {
        console.log('Authentication failed - token expired or invalid');

        // Clear the invalid token
        localStorage.removeItem('token');

        // Dispatch a custom event that AuthContext can listen to
        window.dispatchEvent(new CustomEvent('auth:logout', {
          detail: { reason: 'token_expired' }
        }));

        // Store the current location for redirect after re-login
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== '/login' && currentPath !== '/') {
          localStorage.setItem('redirectAfterLogin', currentPath);
        }
      }
    }

    return Promise.reject(error);
  }
);

export const uploadFitFile = async (file, params = {}, gpxFile = null) => {
  // Get uuid from localStorage
  const uuid = localStorage.getItem('uuid');
  const {
    bikeType = 'road',
    riderWeight = 75,
    bikeWeight = 8.5,
    // Terrain parameters
    terrainType = 'paved',
    terrainPavedPercentage = 100,
    // Road bike parameters
    roadDropsMinSpeed = 30,
    // Road aero parameters
    roadAeroAerobarMinSpeed = 25,
    roadAeroAerobarMaxSpeed = 40,
    roadAeroDropsMinSpeed = 40,
    // TT bike parameters
    ttAeroMinSpeed = 25,
    ttAeroMaxSpeed = 50,
    // Gravel bike parameters
    gravelDropsMinSpeed = 30,
    // What-if parameters
    whatIfBikeType = 'tt',
    whatIfRiderWeight = 75,
    whatIfBikeWeight = 8.5,
    whatIfRoadDropsMinSpeed = 30,
    whatIfRoadAeroAerobarMinSpeed = 25,
    whatIfRoadAeroAerobarMaxSpeed = 40,
    whatIfRoadAeroDropsMinSpeed = 40,
    whatIfTtAeroMinSpeed = 25,
    whatIfTtAeroMaxSpeed = 50,
    whatIfGravelDropsMinSpeed = 30,
    // Physics constants
    airDensity = 1.225,
    rollingResistance = 0.004,
    // CdA values - Road
    cdaRoadHoods = 0.40,
    cdaRoadDrops = 0.32,
    // CdA values - Road aero
    cdaRoadAeroHoods = 0.40,
    cdaRoadAeroAerobar = 0.30,
    cdaRoadAeroDrops = 0.32,
    // CdA values - TT
    cdaTtBase = 0.38,
    cdaTtAero = 0.24,
    // CdA values - Gravel
    cdaGravelHoods = 0.42,
    cdaGravelDrops = 0.34,
    powerRatio = null,
    reusedFileId = null,
    source = 'file_upload',  // 'file_upload' or 'workout_analysis'
    // GPX terrain parameters
    gpxTerrainType = 'paved',
    gpxTerrainPavedPercentage = 100,
    description = null,
  } = params;

  const formData = new FormData();
  formData.append('file', file);
  if (uuid) {
    formData.append('uuid', uuid);
  }

  // Add source parameter
  formData.append('source', source);

  // Add reused file ID if provided
  if (reusedFileId) {
    formData.append('reused_file_id', reusedFileId);
  }

  // Add GPX file if provided
  if (gpxFile) {
    formData.append('gpx_file', gpxFile);
  }

  // Basic parameters
  formData.append('bike_type', bikeType);
  formData.append('rider_weight', riderWeight);
  formData.append('bike_weight', bikeWeight);

  // Terrain parameters
  formData.append('terrain_type', terrainType);
  formData.append('terrain_paved_percentage', terrainPavedPercentage);

  // Road bike parameters
  formData.append('road_drops_min_speed', roadDropsMinSpeed);

  // Road aero parameters
  formData.append('road_aero_aerobar_min_speed', roadAeroAerobarMinSpeed);
  formData.append('road_aero_aerobar_max_speed', roadAeroAerobarMaxSpeed);
  formData.append('road_aero_drops_min_speed', roadAeroDropsMinSpeed);

  // TT bike parameters
  formData.append('tt_aero_min_speed', ttAeroMinSpeed);
  formData.append('tt_aero_max_speed', ttAeroMaxSpeed);

  // Gravel bike parameters
  formData.append('gravel_drops_min_speed', gravelDropsMinSpeed);

  // What-if parameters
  formData.append('what_if_bike_type', whatIfBikeType);
  formData.append('what_if_rider_weight', whatIfRiderWeight);
  formData.append('what_if_bike_weight', whatIfBikeWeight);
  formData.append('what_if_road_drops_min_speed', whatIfRoadDropsMinSpeed);
  formData.append('what_if_road_aero_aerobar_min_speed', whatIfRoadAeroAerobarMinSpeed);
  formData.append('what_if_road_aero_aerobar_max_speed', whatIfRoadAeroAerobarMaxSpeed);
  formData.append('what_if_road_aero_drops_min_speed', whatIfRoadAeroDropsMinSpeed);
  formData.append('what_if_tt_aero_min_speed', whatIfTtAeroMinSpeed);
  formData.append('what_if_tt_aero_max_speed', whatIfTtAeroMaxSpeed);
  formData.append('what_if_gravel_drops_min_speed', whatIfGravelDropsMinSpeed);

  // Physics constants
  formData.append('air_density', airDensity);
  formData.append('rolling_resistance', rollingResistance);

  // CdA values - Road bike
  formData.append('cda_road_hoods', cdaRoadHoods);
  formData.append('cda_road_drops', cdaRoadDrops);

  // CdA values - Road aero
  formData.append('cda_road_aero_hoods', cdaRoadAeroHoods);
  formData.append('cda_road_aero_aerobar', cdaRoadAeroAerobar);
  formData.append('cda_road_aero_drops', cdaRoadAeroDrops);

  // CdA values - TT bike
  formData.append('cda_tt_base', cdaTtBase);
  formData.append('cda_tt_aero', cdaTtAero);

  // CdA values - Gravel bike
  formData.append('cda_gravel_hoods', cdaGravelHoods);
  formData.append('cda_gravel_drops', cdaGravelDrops);

  // Add power ratio if provided
  if (powerRatio !== null) {
    formData.append('power_ratio', powerRatio);
  }

  // Add GPX terrain parameters
  formData.append('gpx_terrain_type', gpxTerrainType);
  formData.append('gpx_terrain_paved_percentage', gpxTerrainPavedPercentage);

  if (description !== null && description !== '') {
    formData.append('description', description);
  }

  const response = await api.post('/upload-fit-v2', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  // Optionally, you can update user session after upload here
  return response.data;
};

export const getSpeedZones = async () => {
  const response = await api.get('/api/analysis/speed-zones');
  return response.data;
};

export const getPowerZones = async () => {
  const response = await api.get('/api/analysis/power-zones');
  return response.data;
};

// Fetch user's saved FIT files
export const getUserFitFiles = async () => {
  const response = await api.get('/auth/my-files/fit');
  return response.data;
};

// Fetch user's saved GPX files
export const getUserGpxFiles = async () => {
  const response = await api.get('/auth/my-files/gpx');
  return response.data;
};

// Download a saved file
export const downloadUserFile = async (fileType, fileId) => {
  const response = await api.get(`/auth/my-files/${fileType}/${fileId}/download`, {
    responseType: 'blob'
  });
  return response.data;
};

// Delete a saved file
export const deleteUserFile = async (fileType, fileId) => {
  const response = await api.delete(`/auth/my-files/${fileType}/${fileId}`);
  return response.data;
};

// Update description of a saved bike file
export const updateBikeFileDescription = async (fileType, fileId, description) => {
  const response = await api.patch(`/auth/my-files/${fileType}/${fileId}/description`, { description: description || null });
  return response.data;
};

// List community bike workouts
export const listCommunityBikeWorkouts = async (params = {}) => {
  const query = new URLSearchParams(params);
  const response = await api.get(`/community/workouts?${query.toString()}`);
  return response.data;
};

// Download a community bike workout as a blob
export const downloadCommunityBikeWorkout = async (workoutId) => {
  const response = await api.get(`/community/workouts/${workoutId}/download`, {
    responseType: 'blob',
  });
  return response.data;
};

// Submit feedback with analysis session reference
export const submitFeedback = async ({ message, analysisSessionId }) => {
  const formData = new FormData();

  // Add message and session ID
  formData.append('message', message);
  formData.append('analysis_session_id', analysisSessionId);

  // Override default Content-Type to let axios set multipart/form-data with boundary
  const response = await api.post('/feedback', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Export GPX segment from analysis session
export const exportGpx = async ({ analysisSessionId, startDistance, endDistance }) => {
  const formData = new FormData();

  formData.append('analysis_session_id', analysisSessionId);
  formData.append('start_distance', startDistance);
  formData.append('end_distance', endDistance);

  const response = await api.post('/export-gpx', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob'
  });

  return response;
};

// Generate workout from analysis session
export const generateWorkout = async ({
  analysisSessionId,
  startTime,
  endTime,
  ftp,
  includeWarmup,
  warmupDuration,
  warmupPower,
  includeCooldown,
  cooldownDuration,
  cooldownPower,
  powerRatio,
  stepPower
}) => {
  const formData = new FormData();

  formData.append('analysis_session_id', analysisSessionId);
  formData.append('start_time', startTime);
  formData.append('end_time', endTime);
  formData.append('ftp', ftp);
  formData.append('include_warmup', includeWarmup);
  formData.append('warmup_duration', warmupDuration);
  formData.append('warmup_power', warmupPower);
  formData.append('include_cooldown', includeCooldown);
  formData.append('cooldown_duration', cooldownDuration);
  formData.append('cooldown_power', cooldownPower);
  formData.append('power_ratio', powerRatio);
  formData.append('step_power', stepPower);

  const response = await api.post('/generate-workout', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// Download Zwift workout file (.zwo format)
export const downloadZwiftWorkout = async ({
  analysisSessionId,
  startTime,
  endTime,
  ftp,
  includeWarmup,
  warmupDuration,
  warmupPower,
  includeCooldown,
  cooldownDuration,
  cooldownPower,
  powerRatio,
  stepPower
}) => {
  const formData = new FormData();

  formData.append('analysis_session_id', analysisSessionId);
  formData.append('start_time', startTime);
  formData.append('end_time', endTime);
  formData.append('ftp', ftp);
  formData.append('include_warmup', includeWarmup);
  formData.append('warmup_duration', warmupDuration);
  formData.append('warmup_power', warmupPower);
  formData.append('include_cooldown', includeCooldown);
  formData.append('cooldown_duration', cooldownDuration);
  formData.append('cooldown_power', cooldownPower);
  formData.append('power_ratio', powerRatio);
  formData.append('step_power', stepPower);

  const response = await api.post('/download-zwift-workout', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob'
  });

  return response;
};

// Download FIT workout file (.fit format)
export const downloadFitWorkout = async ({
  analysisSessionId,
  startTime,
  endTime,
  ftp,
  includeWarmup,
  warmupDuration,
  warmupPower,
  includeCooldown,
  cooldownDuration,
  cooldownPower,
  powerRatio,
  stepPower
}) => {
  const formData = new FormData();

  formData.append('analysis_session_id', analysisSessionId);
  formData.append('start_time', startTime);
  formData.append('end_time', endTime);
  formData.append('ftp', ftp);
  formData.append('include_warmup', includeWarmup);
  formData.append('warmup_duration', warmupDuration);
  formData.append('warmup_power', warmupPower);
  formData.append('include_cooldown', includeCooldown);
  formData.append('cooldown_duration', cooldownDuration);
  formData.append('cooldown_power', cooldownPower);
  formData.append('power_ratio', powerRatio);
  formData.append('step_power', stepPower);

  const response = await api.post('/download-fit-workout', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob'
  });

  return response;
};

// Get user profile information (including FTP)
export const getUserProfile = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// Bike Configuration API endpoints
export const getBikeConfigurations = async () => {
  const response = await api.get('/bike-configs');
  return response;
};

export const createBikeConfiguration = async (configData) => {
  const response = await api.post('/bike-configs', configData);
  return response.data;
};

export const updateBikeConfiguration = async (configId, configData) => {
  const response = await api.put(`/bike-configs/${configId}`, configData);
  return response.data;
};

export const deleteBikeConfiguration = async (configId) => {
  const response = await api.delete(`/bike-configs/${configId}`);
  return response.data;
};

export const createDefaultBikeConfiguration = async (bikeType) => {
  const response = await api.post('/bike-configs/default', { bike_type: bikeType });
  return response.data;
};

// Bike Plan API endpoints
export const buildBikePlan = async (sessionId) => {
  const response = await api.post(`/analysis/${sessionId}/bike-plan`);
  return response.data;
};

export const downloadBikePlanFit = async (sessionId) => {
  const response = await api.post(`/analysis/${sessionId}/download-bike-plan-fit`, {}, {
    responseType: 'blob',
  });
  return response;
};

export const downloadBikePlanZwo = async (sessionId) => {
  const response = await api.post(`/analysis/${sessionId}/download-bike-plan-zwo`, {}, {
    responseType: 'blob',
  });
  return response;
};

export default api;
